/**
 * Project Name: Brainsie eCommerce v4
 * Component: Default Square Image
 * Description: Square image component for infographics
 * Author: Brainsie Team
 * Date Created: October 29, 2025
 */

import React from "react";
import { Box } from "@mui/material";

export default function DefaultSquareImage({
  component,
  section,
  darkMode,
  theme,
  settings = {},
  renderCTAButtons,
}) {
  // Get component data using correct field mapping
  const primaryText = component.Primary_Text || "";
  const secondaryText = component.Secondary_Text || "";
  const descriptiveText = component.Descriptive_Text || "";
  const primaryCTA = component.Primary_CTA || "";
  const secondaryCTA = component.Secondary_CTA || "";

  // Enhanced component settings with comprehensive defaults
  const componentSettings = {
    size: "medium",
    variant: "contained",
    color: "primary",
    fullWidth: false,
    disabled: false,
    fontSize: "",
    fontWeight: "",
    textAlign: "left",
    backgroundColor: "",
    textColor: "",
    borderRadius: "",
    padding: "",
    margin: "",
    elevation: 0,
    customStyles: "",
    ...settings,
    ...(component.Component_Settings
      ? typeof component.Component_Settings === "string"
        ? JSON.parse(component.Component_Settings)
        : component.Component_Settings
      : {}),
  };

  // Enhanced image URL processing with better Media_Files handling
  const getImageUrl = () => {
    if (!component.Media_Files) return null;

    try {
      let mediaFiles;
      if (typeof component.Media_Files === "string") {
        mediaFiles = JSON.parse(component.Media_Files);
      } else {
        mediaFiles = component.Media_Files;
      }

      // Handle both array and object formats
      let mediaFile;
      if (Array.isArray(mediaFiles)) {
        mediaFile = mediaFiles[0];
      } else if (typeof mediaFiles === "object") {
        mediaFile = Object.values(mediaFiles)[0];
      } else {
        mediaFile = mediaFiles;
      }

      if (!mediaFile) return null;

      // Clean up the media file path
      mediaFile = mediaFile.toString().trim();

      // Construct proper URL
      const baseUrl = process.env.BASEURL || "";

      // Handle absolute URLs
      if (mediaFile.startsWith("http")) {
        return mediaFile;
      }

      // Construct relative URL with proper path joining
      return (
        baseUrl + (mediaFile.startsWith("/") ? mediaFile : `/${mediaFile}`)
      );
    } catch (error) {
      console.error("Error processing Media_Files:", error);
      return null;
    }
  };

  const imageUrl = getImageUrl();
  console.log("🖼️ DefaultSquareImage Debug:", {
    component: component,
    mediaFiles: component.Media_Files,
    processedImageUrl: imageUrl,
    baseUrl: process.env.BASEURL,
  });

  // Get comprehensive styling
  const getComponentStyles = () => ({
    mb: componentSettings.margin ? 0 : 2,
    padding: componentSettings.padding || 0,
    margin: componentSettings.margin || "0 0 16px 0",
    backgroundColor: componentSettings.backgroundColor || "transparent",
    borderRadius: componentSettings.borderRadius || "0px",
    textAlign: componentSettings.textAlign || "center",
    width: componentSettings.fullWidth ? "100%" : "auto",
    opacity: componentSettings.disabled ? 0.6 : 1,
    display: "flex",
    flexDirection: "column",
    alignItems:
      componentSettings.textAlign === "left"
        ? "flex-start"
        : componentSettings.textAlign === "right"
        ? "flex-end"
        : "center",
    ...(componentSettings.customStyles
      ? (() => {
          try {
            return typeof componentSettings.customStyles === "string"
              ? JSON.parse(componentSettings.customStyles)
              : componentSettings.customStyles;
          } catch {
            return {};
          }
        })()
      : {}),
  });

  const getImageStyles = () => ({
    width: "100%", // Always use full width for better responsive design
    height: "auto",
    aspectRatio: "1", // Square aspect ratio
    objectFit: "cover",
    borderRadius: componentSettings.borderRadius || "8px",
    boxShadow:
      componentSettings.elevation > 0
        ? `0px ${componentSettings.elevation}px ${
            componentSettings.elevation * 2
          }px rgba(0,0,0,0.12)`
        : "0 4px 12px rgba(0,0,0,0.1)",
    transition: "transform 0.3s ease",
    "&:hover": {
      transform: componentSettings.disabled ? "none" : "scale(1.02)",
    },
    filter: componentSettings.disabled ? "grayscale(100%)" : "none",
  });

  // Show fallback if no image URL is available
  const showFallback = !imageUrl;

  return (
    <Box sx={getComponentStyles()}>
      {/* Primary Text (Image Caption/Title) */}
      {primaryText && primaryText !== "NA" && (
        <Box sx={{ mb: 1, width: "100%" }}>
          <h4
            style={{
              color:
                componentSettings.textColor ||
                (darkMode
                  ? theme.palette.common.white
                  : theme.palette.text.primary),
              margin: "0 0 8px 0",
              fontSize:
                componentSettings.size === "large"
                  ? "1.3rem"
                  : componentSettings.size === "small"
                  ? "0.95rem"
                  : "1.1rem",
              fontWeight: componentSettings.fontWeight || "600",
              textAlign: componentSettings.textAlign || "center",
            }}
          >
            {primaryText}
          </h4>
        </Box>
      )}

      {/* Image or Fallback */}
      {showFallback ? (
        <Box
          sx={{
            ...getImageStyles(),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: darkMode
              ? theme.palette.grey[800]
              : theme.palette.grey[200],
            color: darkMode ? theme.palette.grey[400] : theme.palette.grey[600],
            fontSize: "1rem",
            fontWeight: 500,
          }}
        >
          No Image Available
        </Box>
      ) : (
        <Box
          component="img"
          src={imageUrl}
          alt={
            secondaryText && secondaryText !== "NA"
              ? secondaryText
              : primaryText || "Image"
          }
          sx={getImageStyles()}
          onError={(e) => {
            console.error("🚨 Image load error:", e.target.src);
          }}
        />
      )}

      {/* Secondary Text (Image Description) */}
      {secondaryText && secondaryText !== "NA" && (
        <Box sx={{ mt: 1, width: "100%" }}>
          <p
            style={{
              color:
                componentSettings.textColor ||
                (darkMode
                  ? theme.palette.grey[300]
                  : theme.palette.text.secondary),
              margin: "0",
              fontSize:
                componentSettings.size === "large"
                  ? "1rem"
                  : componentSettings.size === "small"
                  ? "0.8rem"
                  : "0.9rem",
              fontWeight: componentSettings.fontWeight || "normal",
              textAlign: componentSettings.textAlign || "center",
              lineHeight: 1.4,
            }}
          >
            {secondaryText}
          </p>
        </Box>
      )}

      {/* Descriptive Text */}
      {descriptiveText && (
        <Box sx={{ mt: 1, width: "100%" }}>
          <p
            style={{
              color:
                componentSettings.textColor ||
                (darkMode
                  ? theme.palette.grey[400]
                  : theme.palette.text.primary),
              margin: "0",
              fontSize:
                componentSettings.fontSize ||
                (componentSettings.size === "large"
                  ? "1rem"
                  : componentSettings.size === "small"
                  ? "0.875rem"
                  : "0.9rem"),
              fontWeight: componentSettings.fontWeight || "normal",
              textAlign: componentSettings.textAlign || "center",
              lineHeight: 1.5,
            }}
          >
            {descriptiveText}
          </p>
        </Box>
      )}

      {/* CTA Buttons */}
      {(primaryCTA || secondaryCTA) && renderCTAButtons && (
        <Box sx={{ mt: 2, width: "100%" }}>
          {renderCTAButtons(primaryCTA, secondaryCTA)}
        </Box>
      )}
    </Box>
  );
}
